import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getVenues, updateVenues, Venue } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import BodyField from '../components/BodyField';
import { richTextAllowed } from '../utils/richText';
import './Venues.css';

const genId = () => `venue-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
const blank = (): Venue => ({ id: genId(), name: '', addressLine1: '', addressLine2: '', postcode: '', notes: '' });

function Venues() {
  const { plan, features } = useAuth();
  const [items, setItems] = useState<Venue[]>([]);
  const [limit, setLimit] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await getVenues();
        setItems(r.venues);
        setLimit(r.limit);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load venues');
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const update = (id: string, field: keyof Venue, value: string) =>
    setItems(prev => prev.map(v => (v.id === id ? { ...v, [field]: value } : v)));
  const remove = (id: string) => setItems(prev => prev.filter(v => v.id !== id));
  const move = (id: string, dir: -1 | 1) =>
    setItems(prev => {
      const i = prev.findIndex(v => v.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });

  const atLimit = items.length >= limit;
  const isFreeCap = limit === 1;

  const handleSave = async () => {
    setError('');
    setSuccessMessage('');
    setIsSaving(true);
    try {
      const r = await updateVenues(items.filter(v => v.name.trim()));
      setItems(r.venues);
      setLimit(r.limit);
      setSuccessMessage('Venues saved');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save venues');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <div className="loading">Loading venues...</div>;

  return (
    <div className="venues-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Where We Play</h1>
          <p className="page-subtitle">
            Your grounds and training venues. Each gets its own map and directions on the
            public site, and you can point each team at the ground it plays at.
          </p>
        </div>
        <button onClick={handleSave} className="btn btn-primary" disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📍</div>
          <p className="empty-state-text">
            No venues yet. Add your home ground, and if your teams play at more than one, add
            those too.
          </p>
        </div>
      ) : (
        <div className="venue-list">
          {items.map((v, i) => (
            <div key={v.id} className="venue-card">
              <div className="venue-card-head">
                <span className="venue-card-index">{i + 1}</span>
                <input
                  type="text"
                  className="form-input venue-name"
                  placeholder="Venue name, e.g. Cottenham Recreation Ground"
                  value={v.name}
                  onChange={e => update(v.id, 'name', e.target.value)}
                />
                <div className="venue-card-actions">
                  <button type="button" className="btn btn-secondary btn-small" onClick={() => move(v.id, -1)} disabled={i === 0} aria-label="Move up">↑</button>
                  <button type="button" className="btn btn-secondary btn-small" onClick={() => move(v.id, 1)} disabled={i === items.length - 1} aria-label="Move down">↓</button>
                  <button type="button" className="btn btn-danger btn-small" onClick={() => remove(v.id)}>Remove</button>
                </div>
              </div>
              <div className="venue-card-grid">
                <input type="text" className="form-input" placeholder="Address line 1" value={v.addressLine1} onChange={e => update(v.id, 'addressLine1', e.target.value)} />
                <input type="text" className="form-input" placeholder="Address line 2 / town" value={v.addressLine2} onChange={e => update(v.id, 'addressLine2', e.target.value)} />
                <input type="text" className="form-input venue-postcode" placeholder="Postcode" value={v.postcode} onChange={e => update(v.id, 'postcode', e.target.value)} />
              </div>
              <BodyField
                className="form-input"
                rows={2}
                rich={richTextAllowed(plan, features)}
                compact
                placeholder="Notes shown under the address, parking, which pitch, what to bring…"
                value={v.notes}
                onChange={notes => update(v.id, 'notes', notes)}
              />
            </div>
          ))}
        </div>
      )}

      {/* The upgrade moment is visible, not hidden: a free club sees exactly
          what a second venue would cost and where to get it. */}
      {atLimit ? (
        <div className={`venue-limit ${isFreeCap ? 'venue-limit-upsell' : ''}`}>
          {isFreeCap ? (
            <>
              <strong>Play at more than one ground?</strong> The free plan includes one venue.
              The Club plan lets you add up to 10, each with its own map, directions and the teams
              that play there, <Link to="/upgrade">upgrade to Club</Link>.
            </>
          ) : (
            <>You've added the maximum of {limit} venues.</>
          )}
        </div>
      ) : (
        <button type="button" className="btn btn-secondary venue-add" onClick={() => setItems(prev => [...prev, blank()])}>
          + Add {items.length === 0 ? 'your home ground' : 'another ground'}
        </button>
      )}
    </div>
  );
}

export default Venues;
